import { Branch } from "@/app/branchs/branchs.type";
import { Select } from "@/shared/components/organisms/Select";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { t } from "i18next";
import {
  colorsKpi,
  configureExporting,
  generateYearsOptions,
  initializeAmChart,
  Language,
  months,
  getMonthsOptions,
} from "@/shared/utils/chartUtils";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import { useEffect, useState, useMemo } from "react";
import { Option } from "@/shared/components/molecules/OptionsSelect";
import ScrollableSelectContainer from "@/shared/components/organisms/ScrollableSelectContainer";
import { useGetBenefitsByDaysQuery } from "@/app/kpi/kpi.api";
import { faLoader } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export type BenefitsDaysChartProps = {
  branches: Branch[];
};
const BenefitsDaysChart: React.FC<BenefitsDaysChartProps> = ({ branches }) => {
  const [isLoading, setIsLoading] = useState(true);
  //const [branchSelected, setBranchSelected] = useState<Option>();
  const [branchesOptions, setBranchesOptions] = useState<Option[]>([]);
  //const [yearSelected, setYearSelected] = useState<Option>();
  //const [monthSelected, setMonthSelected] = useState<Option>();
  const [currencyEarningsTotalDays, setCurrencyEarningsTotalDays] =
    useState<Option>();
  const [currencyEarningsTotalOptions, setCurrencyEarningsTotalOptions] =
    useState<Option[]>([]);

  const monthsOptions = useMemo(() => getMonthsOptions(t), [t]);

  const yearsOptions = generateYearsOptions(
    new Date().getFullYear() - 10,
    new Date().getFullYear()
  );

  const [branchSelected, setBranchSelected] = useState<Option>(() => {
    const saved = localStorage.getItem("branchSelected");
    return saved ? JSON.parse(saved) : undefined;
  });
  const [monthSelected, setMonthSelected] = useState<Option>(() => {
    const saved = localStorage.getItem("monthSelected");
    return saved ? JSON.parse(saved) : undefined;
  });
  const [yearSelected, setYearSelected] = useState<Option>(() => {
    const saved = localStorage.getItem("yearSelected");
    return saved ? JSON.parse(saved) : undefined;
  });

  const { data: dataChart } = useGetBenefitsByDaysQuery({
    filial: branchSelected?.id,
    currency_request: currencyEarningsTotalDays?.id,
    year: yearSelected?.id,
    month: monthSelected?.id,
  });

  useEffect(() => {
    if (branches) {
      setBranchesOptions(
        branches.map((branch: Branch) => ({
          id: branch.id,
          name: branch.branch,
        }))
      );
    }
  }, [branches]);

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    if (branchesOptions.length > 0 && !branchSelected) {
      setBranchSelected(branchesOptions[0]);
    }
    if (currencyEarningsTotalOptions.length > 0 && !currencyEarningsTotalDays) {
      setCurrencyEarningsTotalDays(currencyEarningsTotalOptions[0]);
    }
    if (yearsOptions.length > 0 && !yearSelected) {
      const defaultYear = yearsOptions.find((year) => year.id === currentYear);
      setYearSelected(defaultYear || yearsOptions[0]);
    }
    if (monthsOptions.length > 0 && !monthSelected) {
      const defaultMonth = monthsOptions.find(
        (month) => month.id === currentMonth
      );
      setMonthSelected(defaultMonth || monthsOptions[0]);
    }
  }, [
    branchesOptions,
    yearsOptions,
    monthsOptions,
    branchSelected,
    yearSelected,
    monthSelected,
  ]);

  useEffect(() => {
    if (dataChart) {
      const transformDataChart = dataChart.data.map(
        (item: any, index: number) => {
          return {
            category: index,
            value: item,
          };
        }
      );

      const maxValue = Math.max(
        ...dataChart.data.map((item: any) => item.value)
      );
      const minValue = Math.min(
        ...dataChart.data.map((item: any) => item.value)
      );

      const currencies = Object.keys(dataChart.currency).map((key) => ({
        id: key,
        name: dataChart.currency[key],
      }));

      setCurrencyEarningsTotalOptions(currencies);

      createBenefitsDays(
        transformDataChart,
        minValue,
        maxValue,
        dataChart.currency_request
      );
    }
  }, [dataChart]);

  function createBenefitsDays(
    data: any,
    minValue: any,
    maxValue: any,
    currency: any
  ) {
    am5.ready(function () {
      const root = initializeAmChart("chart_benefits_total_days");

      root.setThemes([am5themes_Animated.new(root)]);

      if (root._logo) {
        root._logo.dispose();
      }

      // Verificar si no hay datos
      if (!data || data.length === 0) {
        console.log("No data available, displaying message.");
        root.container.children.push(
          am5.Label.new(root, {
            text: "Información no disponible",
            fontSize: 20,
            fill: am5.color("#cbcac8"),
            centerX: am5.p50,
            centerY: am5.p50,
            x: am5.p50,
            y: am5.p50,
          })
        );

        setTimeout(() => {
          setIsLoading(false);
        }, 1000);
        return;
      }

      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: true,
          panY: false,
          wheelX: undefined,
          wheelY: undefined,
          pinchZoomX: true,
          paddingLeft: 0,
          paddingRight: 1,
        })
      );

      const cursor = chart.set("cursor", am5xy.XYCursor.new(root, {}));
      cursor.lineY.set("visible", false);

      const xRenderer = am5xy.AxisRendererX.new(root, {
        minGridDistance: 18,
        minorGridEnabled: true,
      });

      xRenderer.labels.template.setAll({
        rotation: 360, // Ajustar la rotación a -45 grados
        centerY: am5.p50,
        centerX: am5.p100,
        paddingRight: 0,
      });

      xRenderer.grid.template.setAll({
        location: 1,
        visible: false,
      });

      const xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          maxDeviation: 0.3,
          categoryField: "category",
          renderer: xRenderer,
          tooltip: am5.Tooltip.new(root, {}),
        })
      );

      const yRenderer = am5xy.AxisRendererY.new(root, {
        strokeOpacity: 0.1,
      });

      const yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: yRenderer,
          min: minValue < 0 ? minValue : 0,
          max: maxValue < 5 ? 5 : maxValue,
          strictMinMax: true,
          numberFormat: "#",
        })
      );

      // Configurar las etiquetas del eje X para que sean de color gris
      xAxis.get("renderer").labels.template.setAll({
        fontSize: "10px",
        fill: am5.color(0x6b7280), // text-gray-500
      });

      // Configurar las etiquetas del eje Y para que sean de color gris
      yAxis.get("renderer").labels.template.setAll({
        fontSize: "10px",
        fill: am5.color(0x6b7280), // text-gray-500
      });

      const series = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: "Series 1",
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: "value",
          sequencedInterpolation: true,
          categoryXField: "category",
          tooltip: am5.Tooltip.new(root, {
            labelText: `${t(
              "share.views.benefits"
            )} {valueY} ${currency.toUpperCase()}`,
          }),
        })
      );

      series.columns.template.setAll({
        tooltipText: "{name}, {categoryX}: {valueY}" + currency,
        cornerRadiusTL: 2,
        cornerRadiusTR: 2,
        strokeOpacity: 0,
        fill: am5.color("#68478D"),
      });

      xAxis.data.setAll(data);
      series.data.setAll(data);

      configureExporting(root, data);

      setTimeout(() => {
        setIsLoading(false);
      }, 500);

      series.appear(1000);
      chart.appear(1000, 100);
    }); // end am5.ready()
  }

  return (
    <>
      <h2 className="text-center font-bold leading-6 text-gray-900">
        {t("user.views.main_title_benefits_total_diary")}
      </h2>
      <ScrollableSelectContainer>
        <Select
          name="branch"
          selected={branchSelected}
          setSelected={setBranchSelected}
          options={branchesOptions}
          tittle={t("user.views.branch")}
        />
        <Select
          name="currency"
          selected={currencyEarningsTotalDays}
          setSelected={setCurrencyEarningsTotalDays}
          options={currencyEarningsTotalOptions}
          tittle={t("user.views.currency")}
        />
        <Select
          name="month"
          selected={monthSelected}
          setSelected={setMonthSelected}
          options={monthsOptions}
          tittle={t("user.views.month")}
        />
        <Select
          name="year"
          selected={yearSelected}
          setSelected={setYearSelected}
          options={yearsOptions}
          tittle={t("user.views.year")}
        />
      </ScrollableSelectContainer>

      <div
        style={{ position: "relative" }}
        className="xl:mt-2 md:mt-16 sm:mt-10"
      >
        {isLoading && (
          <div className="flex justify-center items-center h-90 mt-20">
            <FontAwesomeIcon icon={faLoader} spin className="text-6xl" />
          </div>
        )}
        <div
          id="chart_benefits_total_days"
          style={{ width: "100%", height: "400px" }}
        ></div>
      </div>
    </>
  );
};

export default BenefitsDaysChart;
